import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Select, MenuItem, ListSubheader, FormControl, InputLabel, IconButton, Tooltip } from '@material-ui/core';
import Settings from '@material-ui/icons/Settings';
import {
  getGridFilters,
  saveGridFilter,
  reorderGridFilters,
  toggleGridFilterActive,
} from '../../services';

import ManageFiltersModal from './ManageFiltersModal';
import SaveFilterModal from './SaveFilterModal';

const ALL_ORDERS_ID = '__all_orders__';
const MY_WORK_ID = '__my_work__';
const PROCESSED_ORDERS_ID = '__processed_orders__';
const DEFAULT_SORT = [{ colId: 'hazardScore', sort: 'desc' }];
const STORAGE_KEY_PREFIX = 'gridFilterDirty_';

const normalize = obj => JSON.stringify(obj || {}, (key, value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const sorted = {};
    Object.keys(value).sort().forEach(k => { sorted[k] = value[k]; });
    return sorted;
  }
  return value;
});

const GridFilterBar = ({ gridNameId, gridApi, myWorkFilterModel, processedOrdersFilterModel, myWorkLabel = 'My Work', processedOrdersLabel = 'Processed Orders', defaultToAllOrders = false }) => {
  const [filters, setFilters] = useState([]);
  const [selectedFilterId, setSelectedFilterId] = useState(myWorkFilterModel && !defaultToAllOrders ? MY_WORK_ID : ALL_ORDERS_ID);
  const [isDirty, setIsDirty] = useState(false);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveMode, setSaveMode] = useState('create'); // 'create' | 'edit'
  const filtersRef = useRef([]);
  const skipDirtyCheckRef = useRef(false);
  const selectedFilterIdRef = useRef(selectedFilterId);
  const dirtyRestoredRef = useRef(false);

  const storageKey = STORAGE_KEY_PREFIX + gridNameId;

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    selectedFilterIdRef.current = selectedFilterId;
  }, [selectedFilterId]);

  const getCleanFilterModel = useCallback((filterId) => {
    if (filterId === ALL_ORDERS_ID) return null;
    if (filterId === MY_WORK_ID) return myWorkFilterModel || null;
    if (filterId === PROCESSED_ORDERS_ID) return processedOrdersFilterModel || null;
    const saved = filtersRef.current.find(f => f.id === filterId);
    if (saved && saved.filterValue) {
      try { return JSON.parse(saved.filterValue); } catch { return null; }
    }
    return null;
  }, [myWorkFilterModel, processedOrdersFilterModel]);

  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.filterModel) {
          setIsDirty(true);
          dirtyRestoredRef.current = true;
          if (parsed.baseFilterId) {
            setSelectedFilterId(parsed.baseFilterId);
          }
        }
      } catch { /* ignore corrupt data */ }
    }
  }, [storageKey]);

  useEffect(() => {
    if (myWorkFilterModel && !defaultToAllOrders && selectedFilterId === ALL_ORDERS_ID && !isDirty) {
      setSelectedFilterId(MY_WORK_ID);
    }
  }, [myWorkFilterModel]);

  const fetchFilters = useCallback(async () => {
    try {
      const data = await getGridFilters(gridNameId);
      setFilters(
        (data || []).map(f => ({
          ...f,
          id: f.userGridFilterId,
        })),
      );
    } catch (error) {
      console.error('Error fetching grid filters:', error);
    }
  }, [gridNameId]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    if (!gridApi) return;

    if (dirtyRestoredRef.current) {
      dirtyRestoredRef.current = false;
      try {
        const stored = JSON.parse(localStorage.getItem(storageKey));
        if (stored && stored.filterModel) {
          skipDirtyCheckRef.current = true;
          gridApi.setFilterModel(stored.filterModel);
          gridApi.setSortModel(stored.sortModel || DEFAULT_SORT);
          setTimeout(() => { skipDirtyCheckRef.current = false; }, 100);
          return;
        }
      } catch { /* fall through to normal apply */ }
    }

    skipDirtyCheckRef.current = true;

    if (selectedFilterId === ALL_ORDERS_ID) {
      gridApi.setFilterModel(null);
      gridApi.setSortModel(DEFAULT_SORT);
    } else if (selectedFilterId === MY_WORK_ID) {
      if (myWorkFilterModel) {
        gridApi.setFilterModel(myWorkFilterModel);
      }
      gridApi.setSortModel(DEFAULT_SORT);
    } else if (selectedFilterId === PROCESSED_ORDERS_ID) {
      if (processedOrdersFilterModel) {
        gridApi.setFilterModel(processedOrdersFilterModel);
      }
      gridApi.setSortModel(DEFAULT_SORT);
    } else {
      const selected = filtersRef.current.find(f => f.id === selectedFilterId);
      if (selected) {
        try {
          const filterModel = selected.filterValue ? JSON.parse(selected.filterValue) : null;
          const sortModel = selected.sortValue ? JSON.parse(selected.sortValue) : null;
          gridApi.setFilterModel(filterModel && Object.keys(filterModel).length > 0 ? filterModel : null);
          gridApi.setSortModel(sortModel || null);
        } catch (e) {
          console.error('Error applying filter:', e);
        }
      }
    }

    setTimeout(() => { skipDirtyCheckRef.current = false; }, 100);
  }, [selectedFilterId, gridApi]);

  useEffect(() => {
    if (!gridApi) return;

    const onFilterChanged = () => {
      if (skipDirtyCheckRef.current) return;

      const currentModel = gridApi.getFilterModel();
      const cleanModel = getCleanFilterModel(selectedFilterIdRef.current);

      if (normalize(currentModel) !== normalize(cleanModel)) {
        setIsDirty(true);
        const sortModel = gridApi.getSortModel();
        try {
          localStorage.setItem(storageKey, JSON.stringify({
            baseFilterId: selectedFilterIdRef.current,
            filterModel: currentModel,
            sortModel,
          }));
        } catch { /* quota exceeded — dirty state still tracked in memory */ }
      } else {
        setIsDirty(false);
        try { localStorage.removeItem(storageKey); } catch {}
      }
    };

    gridApi.addEventListener('filterChanged', onFilterChanged);
    gridApi.addEventListener('sortChanged', onFilterChanged);
    return () => {
      try {
        gridApi.removeEventListener('filterChanged', onFilterChanged);
        gridApi.removeEventListener('sortChanged', onFilterChanged);
      } catch {}
    };
  }, [gridApi, getCleanFilterModel, storageKey]);

  const handleFilterChange = e => {
    setIsDirty(false);
    localStorage.removeItem(storageKey);
    setSelectedFilterId(e.target.value);
  };

  const handleSaveClick = () => {
    setSaveMode('create');
    setSaveModalOpen(true);
  };

  const handleEditClick = () => {
    setSaveMode('edit');
    setSaveModalOpen(true);
  };

  const handleSaveFilter = async (filterName, filterDescription) => {
    if (!gridApi) return;

    const filterModel = gridApi.getFilterModel();
    const sortModel = gridApi.getSortModel();
    const filterValue = JSON.stringify(filterModel || {});
    const sortValue = sortModel && sortModel.length > 0 ? JSON.stringify(sortModel) : null;

    try {
      const isEdit = saveMode === 'edit' && selectedFilterId && !isSystemFilter;
      await saveGridFilter({ id: isEdit ? selectedFilterId : null, gridNameLookupId: gridNameId, filterName, filterDescription, filterValue, sortValue });
      setSaveModalOpen(false);
      setIsDirty(false);
      localStorage.removeItem(storageKey);
      await fetchFilters();
    } catch (error) {
      console.error('Error saving filter:', error);
    }
  };

  const handleManageSave = async updatedFilters => {
    try {
      const orderedIds = updatedFilters.map(f => f.id);
      await reorderGridFilters(orderedIds);

      for (const updated of updatedFilters) {
        const original = filters.find(f => f.id === updated.id);
        if (original && original.isActive !== updated.isActive) {
          await toggleGridFilterActive(updated.id);
        }
      }

      await fetchFilters();

      const isSystemFilter = selectedFilterId === ALL_ORDERS_ID || selectedFilterId === MY_WORK_ID || selectedFilterId === PROCESSED_ORDERS_ID;
      if (!isSystemFilter) {
        const selectedStillActive = updatedFilters.find(f => f.id === selectedFilterId && f.isActive);
        if (!selectedStillActive) {
          setIsDirty(false);
          localStorage.removeItem(storageKey);
          setSelectedFilterId(myWorkFilterModel ? MY_WORK_ID : ALL_ORDERS_ID);
        }
      }
    } catch (error) {
      console.error('Error saving filter changes:', error);
    }
  };

  const getDisplayLabel = (val) => {
    if (isDirty) return 'Unsaved Filter';
    if (val === MY_WORK_ID) return myWorkLabel;
    if (val === ALL_ORDERS_ID) return 'All Orders';
    if (val === PROCESSED_ORDERS_ID) return processedOrdersLabel;
    const f = filters.find(fl => fl.id === val);
    return f ? f.filterName : '';
  };

  const selectedFilter = filters.find(f => f.id === selectedFilterId) || null;
  const activeFilters = filters.filter(f => f.isActive);
  const isSystemFilter = selectedFilterId === ALL_ORDERS_ID || selectedFilterId === MY_WORK_ID || selectedFilterId === PROCESSED_ORDERS_ID;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '8px 0',
        marginBottom: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={selectedFilterId}
            onChange={handleFilterChange}
            label="Filter"
            renderValue={getDisplayLabel}
          >
            <ListSubheader key="filter-default-header" style={{ lineHeight: '28px', fontSize: '0.75rem', fontWeight: 600, color: '#315382', borderBottom: '1px solid #ccc', pointerEvents: 'none'}}>DEFAULT FILTERS</ListSubheader>
            {!defaultToAllOrders && myWorkFilterModel && <MenuItem key="filter-my-work" value={MY_WORK_ID}>{myWorkLabel}</MenuItem>}
            <MenuItem key="filter-all-orders" value={ALL_ORDERS_ID}>All Orders</MenuItem>
            {defaultToAllOrders && myWorkFilterModel && <MenuItem key="filter-my-work" value={MY_WORK_ID}>{myWorkLabel}</MenuItem>}
            {processedOrdersFilterModel && <MenuItem key="filter-processed" value={PROCESSED_ORDERS_ID}>{processedOrdersLabel}</MenuItem>}
            {activeFilters.length > 0 && (
              <ListSubheader key="filter-my-filters-header" style={{ lineHeight: '28px', fontSize: '0.75rem', fontWeight: 600, color: '#315382', borderBottom: '1px solid #ccc', borderTop: '1px solid #ccc', pointerEvents: 'none'}}>MY FILTERS</ListSubheader>
            )}
            {activeFilters.map((f, i) => (
              <MenuItem key={String(f.id ?? `filter-${i}`)} value={f.id}>
                {f.filterName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Manage Filters">
          <IconButton size="small" onClick={() => setManageModalOpen(true)}>
            <Settings style={{ color: '#315382' }} />
          </IconButton>
        </Tooltip>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Tooltip
          title={isSystemFilter && !isDirty ? "Default filters cannot be edited. Use 'Save Current Filter' to create a custom version." : ''}
          disableHoverListener={!(isSystemFilter && !isDirty)}
        >
          <span>
            <Button
              variant="contained"
              style={{ backgroundColor: isSystemFilter && !isDirty ? '#a0a0a0' : '#315382', color: '#fff' }}
              onClick={handleEditClick}
              disabled={isSystemFilter && !isDirty}
            >
              Edit Current Filter
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          style={{ backgroundColor: '#315382', color: '#fff' }}
          onClick={handleSaveClick}
        >
          Save Current Filter
        </Button>
      </div>

      <ManageFiltersModal
        open={manageModalOpen}
        onClose={() => setManageModalOpen(false)}
        filters={filters}
        onSave={handleManageSave}
      />

      <SaveFilterModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onSave={handleSaveFilter}
        editingFilter={saveMode === 'edit' && !isSystemFilter ? selectedFilter : null}
      />
    </div>
  );
};

export default GridFilterBar;
